import { useEffect, useState } from 'react';
import { MessageSquare, RotateCcw, Search } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '@/components/primitives/Button.jsx';
import Input from '@/components/primitives/Input.jsx';
import Skeleton from '@/components/primitives/Skeleton.jsx';
import EmptyState from '@/components/patterns/EmptyState.jsx';
import Tabs from '@/components/patterns/Tabs.jsx';
import formatRelativeTime from '@/lib/dates/formatRelativeTime.js';
import toastManager from '@/lib/toast/toastManager.js';
import hydrateThreads from '@/redux/actions/pieveActions/hydrateThreads.js';
import { restoreThread } from '@/redux/requests/threadArchiveRequests.js';
import { getArchivedThreads, searchSidebarThreads } from '@/redux/requests/threadsRequests.js';
import { archivedThreads, assistantThreadItems, threadSearchResults } from '@/redux/selectors/pieveSelectors/assistantSelectors.js';
import ThreadListItem from './ThreadListItem.jsx';

const SEARCH_DELAY_MS = 250;

/** Every conversation on the assistant home: the active ones with a search by title, and the archived ones with restore. */
const ConversationList = () => {
  const dispatch = useDispatch();
  const items = useSelector(assistantThreadItems);
  const searchRows = useSelector(threadSearchResults);
  const archivedRows = useSelector(archivedThreads);
  const [query, setQuery] = useState('');
  const [searchedQuery, setSearchedQuery] = useState('');
  const [tab, setTab] = useState('active');
  const [isArchivedLoading, setIsArchivedLoading] = useState(false);
  const trimmedQuery = query.trim();

  useEffect(() => {
    if (!trimmedQuery) {
      return undefined;
    }
    const timer = setTimeout(async () => {
      await searchSidebarThreads({ search: trimmedQuery });
      setSearchedQuery(trimmedQuery);
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [trimmedQuery]);

  const changeTab = async (nextTab) => {
    setTab(nextTab);
    if (nextTab === 'archived') {
      setIsArchivedLoading(true);
      await getArchivedThreads();
      setIsArchivedLoading(false);
    }
  };

  const restore = async (threadId) => {
    const res = await restoreThread({ threadId });
    if (res?.success === false) {
      toastManager.add({ title: 'Not restored', description: res.message || 'The conversation could not be restored.', type: 'error' });
      return;
    }
    toastManager.add({ title: 'Restored', description: 'The conversation is back in your list.', type: 'success' });
    dispatch(hydrateThreads({ skipEvents: true }));
  };

  const searchItems = searchRows.map((row) => ({ id: row.threadId, label: row.title, threadType: row.threadType, updatedAt: row.updatedAt, isCompany: row.isCompany }));
  const visibleItems = trimmedQuery ? searchItems : items;
  const isSearching = Boolean(trimmedQuery) && searchedQuery !== trimmedQuery;

  const activePanel = (
    <div className="assistant-conversations__panel">
      <Input icon={Search} type="search" placeholder="Search by title" aria-label="Search conversations" value={query} onChange={(event) => setQuery(event.target.value)} />
      {isSearching ? (
        <div className="assistant-conversations__loading">
          <Skeleton />
          <Skeleton />
        </div>
      ) : visibleItems.length > 0 ? (
        <ul className="assistant-conversations__list">
          {visibleItems.map((item) => (
            <ThreadListItem key={item.id} item={item} />
          ))}
        </ul>
      ) : trimmedQuery ? (
        <p className="assistant-conversations__empty">No conversation title matches &quot;{trimmedQuery}&quot;.</p>
      ) : (
        <EmptyState icon={MessageSquare} title="No conversations yet" description="Your requests and Pieve's work on them are kept here." />
      )}
    </div>
  );

  const archivedPanel = (
    <div className="assistant-conversations__panel">
      {isArchivedLoading && archivedRows.length === 0 ? (
        <div className="assistant-conversations__loading">
          <Skeleton />
          <Skeleton />
        </div>
      ) : archivedRows.length > 0 ? (
        <ul className="assistant-conversations__list">
          {archivedRows.map((row) => {
            return (
              <li key={row.id} className="assistant-archived-item">
                <span className="assistant-archived-item__title">{row.title}</span>
                <span className="assistant-thread-item__time">{formatRelativeTime(row.archivedAt)}</span>
                <Button variant="ghost" size="sm" iconStart={RotateCcw} onClick={() => restore(row.id)}>
                  Restore
                </Button>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="assistant-conversations__empty">Nothing archived. Archive a conversation from its menu to move it here.</p>
      )}
    </div>
  );

  return (
    <section className="assistant-conversations" aria-labelledby="assistant-conversations-title">
      <h2 id="assistant-conversations-title" className="assistant-section-title">
        Conversations
      </h2>
      <Tabs
        label="Conversations"
        value={tab}
        onValueChange={changeTab}
        items={[
          { value: 'active', label: 'Active', panel: activePanel },
          { value: 'archived', label: 'Archived', panel: archivedPanel }
        ]}
      />
    </section>
  );
};

export default ConversationList;
